import { SESSION_ID } from '@/constants/params';
import type { ApiError } from '@/services/apiClient';
import { getSessionIdFromUrlParams } from '@/utils/location';
import { logWarn } from '@/utils/logger';
import { sanitizeMerchantRedirectUrl } from '@/utils/merchantRedirectUrl';

const PERSISTED_REDIRECT_URL_KEY_PREFIX = 'checkout_redirect_url:';

const resolveCheckoutSessionId = (): string => {
  try {
    const search = new URLSearchParams(window.location.search);
    return search.get(SESSION_ID) || getSessionIdFromUrlParams() || '';
  } catch {
    return '';
  }
};

export const extractRedirectUrlFromApiError = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const body = (error as ApiError).info;
  if (!body || typeof body !== 'object' || !('redirectUrl' in body)) return undefined;
  const redirectUrl = (body as { redirectUrl?: unknown }).redirectUrl;
  return typeof redirectUrl === 'string' && redirectUrl ? redirectUrl : undefined;
};

const getStorageKeyForCurrentSession = (): string | null => {
  const sessionId = resolveCheckoutSessionId();
  if (!sessionId) return null;
  return `${PERSISTED_REDIRECT_URL_KEY_PREFIX}${sessionId}`;
};

export const persistRedirectUrl = (redirectUrl?: string | null): void => {
  const safeRedirectUrl = sanitizeMerchantRedirectUrl(redirectUrl);
  if (!safeRedirectUrl) return;

  const storageKey = getStorageKeyForCurrentSession();
  if (!storageKey) return;

  try {
    localStorage.setItem(storageKey, safeRedirectUrl);
  } catch (error) {
    // Storage may be unavailable (private mode, third-party iframe, quota).
    logWarn('Failed to persist redirect URL to localStorage', {
      key: storageKey,
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

export const getPersistedRedirectUrl = (): string => {
  const storageKey = getStorageKeyForCurrentSession();
  if (!storageKey) return '';

  try {
    return sanitizeMerchantRedirectUrl(localStorage.getItem(storageKey)) ?? '';
  } catch {
    return '';
  }
};
